import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import { alpha } from '@mui/material/styles';
import {
  Timestamp,
  collection,
  collectionGroup,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import LeadCompactCard from './LeadCompactCard';

type DirectoryUser = { id: string; name?: string | null; email?: string | null; roleId?: string | null; role?: string | null };
type LeadStatusDef = { id: string; label?: string | null; name?: string | null; color?: string | null };

const METRIC_KEYS = [
  'newLeads',
  'visits',
  'revisit',
  'brokerVisit',
  'c2cVisit',
  'callVisit',
  'walkInVisit',
  'referenceVisit',
  'otherVisit',
] as const;
type MetricKey = (typeof METRIC_KEYS)[number];

const METRIC_LABELS: Record<MetricKey, string> = {
  newLeads: 'New leads',
  visits: 'Visits',
  revisit: 'Revisits',
  brokerVisit: 'Broker visits',
  c2cVisit: 'C2C visits',
  callVisit: 'Call visits',
  walkInVisit: 'Walk-in visits',
  referenceVisit: 'Reference visits',
  otherVisit: 'Other visits',
};

type DailyRow = {
  key: string; // YYYY-MM-DD
  newLeads: number;
  visits: number;
  revisit: number;
  brokerVisit: number;
  c2cVisit: number;
  callVisit: number;
  walkInVisit: number;
  referenceVisit: number;
  otherVisit: number;
};

type CompareRow = DailyRow & { label: string };

type DetailMap = Record<string, Record<MetricKey, Record<string, number>>>;

type RangeResult = { detail: DetailMap; newSourceDetail: Record<string, Record<string, number>>; leadCache: Record<string, any>; rows: DailyRow[] };

function ymdKey(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function dateFromDayKey(key: string): Date {
  const [y, m, d] = key.split('-').map((n) => Number(n));
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function toDateMaybe(v: any): Date | null {
  if (!v) return null;
  if (v?.toDate) {
    const d = v.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (v instanceof Date) return !Number.isNaN(v.getTime()) ? v : null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return !Number.isNaN(d.getTime()) ? d : null;
  }
  return null;
}

const sumBucket = (m: Record<string, number>) => Object.values(m).reduce((a, b) => a + (Number(b) || 0), 0);

const zeroTotals = () => ({
  newLeads: 0,
  visits: 0,
  revisit: 0,
  brokerVisit: 0,
  c2cVisit: 0,
  callVisit: 0,
  walkInVisit: 0,
  referenceVisit: 0,
  otherVisit: 0,
});

function rowsFromDetail(detail: DetailMap): DailyRow[] {
  return Object.entries(detail)
    .map(([k, m]) => ({
      key: k,
      newLeads: sumBucket(m.newLeads || {}),
      visits: sumBucket(m.visits || {}),
      revisit: sumBucket(m.revisit || {}),
      brokerVisit: sumBucket(m.brokerVisit || {}),
      c2cVisit: sumBucket(m.c2cVisit || {}),
      callVisit: sumBucket(m.callVisit || {}),
      walkInVisit: sumBucket(m.walkInVisit || {}),
      referenceVisit: sumBucket(m.referenceVisit || {}),
      otherVisit: sumBucket(m.otherVisit || {}),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

function listDayKeys(rangeStart: Date, rangeEndExclusive: Date): string[] {
  const keys: string[] = [];
  const cursor = new Date(rangeStart);
  cursor.setHours(0, 0, 0, 0);
  while (cursor.getTime() < rangeEndExclusive.getTime()) {
    keys.push(ymdKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

// Fetch lead docs by id in parallel batches (Firestore 'in' caps at 10 ids per query).
async function fetchLeadsByIds(ids: string[]): Promise<Record<string, any>> {
  const out: Record<string, any> = {};
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));
  await Promise.all(
    chunks.map(async (batch) => {
      if (batch.length === 0) return;
      const snap = await getDocs(query(collection(db, 'leads'), where(documentId(), 'in', batch)));
      snap.forEach((d) => {
        out[d.id] = { id: d.id, ...(d.data() as any) };
      });
    })
  );
  return out;
}

// Live compute: walks activities/leads directly. Used only as a fallback for days the
// nightly summary hasn't produced yet.
async function loadForRange(rangeStart: Date, rangeEnd: Date): Promise<RangeResult> {
  const detail: DetailMap = {};
  const newSourceDetail: Record<string, Record<string, number>> = {};
  const leadCache: Record<string, any> = {};
  const brokerByIdInclude = new Map<string, boolean>();
  const excludedBrokerTokens = new Set<string>();

  const bump = (dayKey: string, metric: MetricKey, leadId: string, n = 1) => {
    if (!detail[dayKey]) {
      detail[dayKey] = Object.fromEntries(METRIC_KEYS.map((mk) => [mk, {}])) as DetailMap[string];
    }
    detail[dayKey][metric][leadId] = (detail[dayKey][metric][leadId] || 0) + n;
  };

  const sourceLabel = (v: any) => {
    const s = String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!s) return 'Unknown';
    if (s === 'walk in' || s === 'walk-in') return 'Walk In';
    if (s === 'with broker' || s === 'with brocker' || s === 'broker' || s === 'brocker') return 'With Broker';
    if (s === 'social ad' || s === 'social ads' || s === 'social lead' || s === 'meta lead') return 'Social Ad';
    if (s === 'whatsapp' || s === 'whats app' || s === 'whatsapp lead' || s === 'wa') return 'WhatsApp';
    return String(v || 'Unknown').trim();
  };
  const normalizeRefToken = (v: any) => String(v || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

  const brokersSnap = await getDocs(collection(db, 'brokers'));
  brokersSnap.forEach((d) => {
    const b = d.data() as any;
    const include = b?.includeInBrokerAnalytics !== false;
    brokerByIdInclude.set(d.id, include);
    if (!include) {
      [b?.brokerName, b?.agencyName, b?.displayName]
        .map(normalizeRefToken)
        .filter(Boolean)
        .forEach((t) => excludedBrokerTokens.add(t));
    }
  });

  // 1) New leads in range.
  const leadSnap = await getDocs(
    query(
      collection(db, 'leads'),
      where('createdAt', '>=', Timestamp.fromDate(rangeStart)),
      where('createdAt', '<', Timestamp.fromDate(rangeEnd))
    )
  );
  leadSnap.forEach((d) => {
    const data = d.data() as any;
    leadCache[d.id] = { id: d.id, ...data };
    const dt = toDateMaybe(data?.createdAt);
    if (!dt) return;
    const key = ymdKey(dt);
    bump(key, 'newLeads', d.id, 1);
    const src = sourceLabel(data?.source);
    const day = (newSourceDetail[key] = newSourceDetail[key] || {});
    day[src] = (day[src] || 0) + 1;
  });

  // 2) Visits in range + connected calls for inference.
  const lookbackStart = new Date(rangeStart);
  lookbackStart.setDate(lookbackStart.getDate() - 30);
  const visitSnap = await getDocs(
    query(
      collectionGroup(db, 'activities'),
      where('type', '==', 'visit'),
      where('at', '>=', Timestamp.fromDate(rangeStart)),
      where('at', '<', Timestamp.fromDate(rangeEnd))
    )
  );
  const callSnap = await getDocs(
    query(
      collectionGroup(db, 'activities'),
      where('type', '==', 'call'),
      where('at', '>=', Timestamp.fromDate(lookbackStart)),
      where('at', '<', Timestamp.fromDate(rangeEnd))
    )
  );

  const visitByLead: Record<string, Array<{ id: string; at: Date; dayKey: string; data: any }>> = {};
  const visitDocLeadIds = new Set<string>();
  visitSnap.forEach((docSnap) => {
    const at = toDateMaybe((docSnap.data() as any)?.at);
    if (!at) return;
    const leadId = docSnap.ref.parent.parent?.id;
    if (!leadId) return;
    const dayKey = ymdKey(at);
    visitDocLeadIds.add(leadId);
    (visitByLead[leadId] = visitByLead[leadId] || []).push({ id: docSnap.id, at, dayKey, data: docSnap.data() as any });
  });

  // The selected range may contain only a lead's second or later visit.
  // Load the true first visit so revisits are not classified by lead source.
  const firstVisitCache = new Map<string, Promise<{ id: string; at: number } | null>>();
  const loadFirstVisit = (leadId: string) => {
    const cached = firstVisitCache.get(leadId);
    if (cached) return cached;
    const request = getDocs(
      query(collection(db, 'leads', leadId, 'activities'), where('type', '==', 'visit'), orderBy('at', 'asc'), limit(1))
    ).then((firstVisitSnap) => {
      const firstVisitDoc = firstVisitSnap.docs[0];
      const firstVisitAt = toDateMaybe(firstVisitDoc?.data()?.at);
      if (!firstVisitDoc || !firstVisitAt) return null;
      return { id: firstVisitDoc.id, at: firstVisitAt.getTime() };
    });
    firstVisitCache.set(leadId, request);
    return request;
  };
  const firstVisitByLead = new Map<string, { id: string; at: number }>();
  await Promise.all(
    Array.from(visitDocLeadIds).map(async (leadId) => {
      const firstVisit = await loadFirstVisit(leadId);
      if (firstVisit) firstVisitByLead.set(leadId, firstVisit);
    })
  );

  const connectedCallsByLead: Record<string, Date[]> = {};
  const callDocLeadIds = new Set<string>();
  callSnap.forEach((docSnap) => {
    const data = docSnap.data() as any;
    const at = toDateMaybe(data?.at);
    if (!at) return;
    const leadId = docSnap.ref.parent.parent?.id;
    if (!leadId) return;
    callDocLeadIds.add(leadId);
    const outcome = String(data?.callOutcome || '').trim().toLowerCase();
    if (outcome !== 'connected') return;
    (connectedCallsByLead[leadId] = connectedCallsByLead[leadId] || []).push(at);
  });

  // 3) Fetch leads needed for categorization (parallel batches).
  const leadIdsNeeded = Array.from(new Set<string>([...Array.from(visitDocLeadIds), ...Array.from(callDocLeadIds)]));
  const fetchedLeads = await fetchLeadsByIds(leadIdsNeeded);
  const leadMap = new Map<string, any>();
  Object.entries(fetchedLeads).forEach(([id, row]) => {
    leadMap.set(id, row);
    leadCache[id] = row;
  });

  const normalizeSourceToken = (v: any) => String(v || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  const isWalkIn = (lead: any) => normalizeSourceToken(lead?.source) === 'walk in';
  const isSourceBroker = (lead: any) => {
    const s = normalizeSourceToken(lead?.source);
    return s === 'with broker' || s === 'with brocker' || s === 'broker' || s === 'brocker';
  };
  const isExcludedBrokerRefToken = (v: any) => {
    const t = normalizeRefToken(v);
    return (
      t === 'social ad' ||
      t === 'insta reels' ||
      t === 'na' ||
      t === 'n a' ||
      t === 'n/a' ||
      t === 'not available' ||
      t === 'none' ||
      t === '-' ||
      t === '--' ||
      t === 'self' ||
      t === 'self lead'
    );
  };
  const isExcludedBrokerByConfig = (lead: any) => {
    const id = String(lead?.referredBrokerId || lead?.brokerId || '').trim();
    if (id && brokerByIdInclude.get(id) === false) return true;
    const names = [lead?.referredBrokerName, lead?.referredAgencyName, lead?.brokerName, lead?.refByName];
    return names.some((v) => {
      const t = normalizeRefToken(v);
      return Boolean(t) && excludedBrokerTokens.has(t);
    });
  };
  const isBroker = (lead: any) =>
    !(
      isExcludedBrokerByConfig(lead) ||
      isExcludedBrokerRefToken(lead?.referredBrokerName) ||
      isExcludedBrokerRefToken(lead?.referredAgencyName) ||
      isExcludedBrokerRefToken(lead?.brokerName) ||
      isExcludedBrokerRefToken(lead?.refByName) ||
      String(lead?.refByType || '').trim().toLowerCase() === 'c2c'
    ) &&
    Boolean(
      isSourceBroker(lead) ||
        (String(lead?.refByType || '').trim().toLowerCase() !== 'reference' &&
          String(lead?.refByType || '').trim().toLowerCase() !== 'c2c' &&
          String(lead?.refByName || '').trim() &&
          !isExcludedBrokerRefToken(lead?.refByName)) ||
        lead?.brokerId ||
        lead?.referredBrokerId ||
        lead?.referredBrokerName ||
        lead?.referredAgencyName ||
        lead?.brokerName
    );
  const isReference = (lead: any) => {
    const refType = String(lead?.refByType || '').trim().toLowerCase();
    if (refType === 'c2c') return false;
    if (refType === 'reference') return true;
    const refByName = String(lead?.refByName || '').trim();
    if (refByName && !isExcludedBrokerRefToken(refByName)) return false;
    return Boolean(refByName);
  };
  const isC2C = (lead: any) => String(lead?.refByType || '').trim().toLowerCase() === 'c2c';
  const isVisitBrokerByRefChange = (visitData: any) => {
    const toName = String(visitData?.refByToName || '').trim();
    const toRefName = String(visitData?.refByToRefName || '').trim();
    const toType = String(visitData?.refByToType || '').trim().toLowerCase();
    const toNameToken = normalizeRefToken(toName);
    const toRefToken = normalizeRefToken(toRefName);
    const hasExcludedConfiguredBroker =
      (toNameToken && excludedBrokerTokens.has(toNameToken)) || (toRefToken && excludedBrokerTokens.has(toRefToken));
    if (hasExcludedConfiguredBroker) return false;
    return (
      (toType === 'broker' && !isExcludedBrokerRefToken(toName)) ||
      (toType === 'reference' && !isExcludedBrokerRefToken(toRefName)) ||
      (!toType && ((toName && !isExcludedBrokerRefToken(toName)) || (toRefName && !isExcludedBrokerRefToken(toRefName))))
    );
  };
  const isVisitC2CByRefChange = (visitData: any) => String(visitData?.refByToType || '').trim().toLowerCase() === 'c2c';

  const hasPriorConnectedCall = (leadId: string, firstVisitAt: Date) => {
    const calls = connectedCallsByLead[leadId] || [];
    if (calls.length === 0) return false;
    const min = new Date(firstVisitAt);
    min.setDate(min.getDate() - 30);
    return calls.some((c) => c.getTime() < firstVisitAt.getTime() && c.getTime() >= min.getTime());
  };

  for (const [leadId, items] of Object.entries(visitByLead)) {
    const lead = leadMap.get(leadId) || null;
    const sorted = items.slice().sort((a, b) => a.at.getTime() - b.at.getTime());
    const firstEverVisit = firstVisitByLead.get(leadId) || (sorted[0] ? { id: sorted[0].id, at: sorted[0].at.getTime() } : null);
    for (let idx = 0; idx < sorted.length; idx++) {
      const v = sorted[idx];
      bump(v.dayKey, 'visits', leadId, 1);
      const isRevisit = Boolean(firstEverVisit) && (v.id !== firstEverVisit?.id || v.at.getTime() > firstEverVisit.at);
      if (isRevisit) {
        bump(v.dayKey, 'revisit', leadId, 1);
        continue;
      }
      if (isVisitBrokerByRefChange(v.data)) {
        bump(v.dayKey, 'brokerVisit', leadId, 1);
      } else if (isVisitC2CByRefChange(v.data)) {
        bump(v.dayKey, 'c2cVisit', leadId, 1);
      } else if (lead && isC2C(lead)) {
        bump(v.dayKey, 'c2cVisit', leadId, 1);
      } else if (lead && isReference(lead)) {
        bump(v.dayKey, 'referenceVisit', leadId, 1);
      } else if (lead && isBroker(lead)) {
        bump(v.dayKey, 'brokerVisit', leadId, 1);
      } else if (lead && isWalkIn(lead)) {
        bump(v.dayKey, 'walkInVisit', leadId, 1);
      } else if (lead && !isWalkIn(lead) && !isBroker(lead) && !isReference(lead) && hasPriorConnectedCall(leadId, v.at)) {
        bump(v.dayKey, 'callVisit', leadId, 1);
      } else {
        bump(v.dayKey, 'otherVisit', leadId, 1);
      }
    }
  }

  return { detail, newSourceDetail, leadCache, rows: rowsFromDetail(detail) };
}

async function loadSummaryRange(rangeStart: Date, rangeEnd: Date): Promise<RangeResult> {
  const startKey = ymdKey(rangeStart);
  const endKey = ymdKey(rangeEnd);
  const newSourceDetail: Record<string, Record<string, number>> = {};
  const snap = await getDocs(
    query(
      collection(db, 'leadVisitDailySummaryLite'),
      where(documentId(), '>=', startKey),
      where(documentId(), '<', endKey),
      orderBy(documentId(), 'asc')
    )
  );
  const rows = snap.docs
    .map((docSnap) => {
      const data = docSnap.data() as any;
      const dayKey = docSnap.id;
      newSourceDetail[dayKey] = Object.fromEntries(
        Object.entries(data?.newSourceCounts || {}).map(([key, count]) => [key, Number(count) || 0])
      );
      const counts = data?.counts || {};
      return {
        key: dayKey,
        newLeads: Number(counts.newLeads) || 0,
        visits: Number(counts.visits) || 0,
        revisit: Number(counts.revisit) || 0,
        brokerVisit: Number(counts.brokerVisit) || 0,
        c2cVisit: Number(counts.c2cVisit) || 0,
        callVisit: Number(counts.callVisit) || 0,
        walkInVisit: Number(counts.walkInVisit) || 0,
        referenceVisit: Number(counts.referenceVisit) || 0,
        otherVisit: Number(counts.otherVisit) || 0,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
  return { detail: {} as DetailMap, newSourceDetail, leadCache: {} as Record<string, any>, rows };
}

// Summary-sourced days carry aggregate counts but no per-lead detail, so rows are merged
// directly (summing overlapping day-keys) rather than being re-derived from `detail` alone
// - deriving from `detail` would silently drop every summary-only day.
function mergeResults(left: RangeResult, right: RangeResult): RangeResult {
  const detail: DetailMap = {};
  const newSourceDetail: Record<string, Record<string, number>> = { ...(left.newSourceDetail || {}) };
  const apply = (source: DetailMap) => {
    Object.entries(source || {}).forEach(([dayKey, metrics]) => {
      if (!detail[dayKey]) {
        detail[dayKey] = Object.fromEntries(METRIC_KEYS.map((mk) => [mk, {}])) as DetailMap[string];
      }
      METRIC_KEYS.forEach((metricKey) => {
        const bucket = metrics?.[metricKey] || {};
        Object.entries(bucket).forEach(([leadId, count]) => {
          detail[dayKey][metricKey][leadId] = (detail[dayKey][metricKey][leadId] || 0) + (Number(count) || 0);
        });
      });
    });
  };
  apply(left.detail || {});
  apply(right.detail || {});
  Object.entries(right.newSourceDetail || {}).forEach(([dayKey, counts]) => {
    const target = (newSourceDetail[dayKey] = { ...(newSourceDetail[dayKey] || {}) });
    Object.entries(counts || {}).forEach(([key, count]) => {
      target[key] = (target[key] || 0) + (Number(count) || 0);
    });
  });

  const rowsByKey = new Map<string, DailyRow>();
  const addRow = (r: DailyRow) => {
    const existing = rowsByKey.get(r.key);
    if (!existing) {
      rowsByKey.set(r.key, { ...r });
      return;
    }
    METRIC_KEYS.forEach((mk) => {
      (existing as any)[mk] += (r as any)[mk];
    });
  };
  (left.rows || []).forEach(addRow);
  (right.rows || []).forEach(addRow);

  return {
    detail,
    newSourceDetail,
    leadCache: { ...(left.leadCache || {}), ...(right.leadCache || {}) },
    rows: Array.from(rowsByKey.values()).sort((a, b) => a.key.localeCompare(b.key)),
  };
}

// One-time backfill for days the nightly job hasn't produced a summary for yet (e.g. months
// before the job existed, or an occasional missed run). Admin-gated server-side; a
// permission-denied here just means the caller isn't admin, so callers should swallow it and
// fall back to live compute for the residual gap.
async function backfillDayRange(startDayKey: string, endDayKey: string): Promise<void> {
  const rebuild = httpsCallable(functions, 'rebuildLeadVisitDailySummary');
  const chunks: Array<{ startDayKey: string; endDayKey: string }> = [];
  let cursor = dateFromDayKey(startDayKey);
  const endDate = dateFromDayKey(endDayKey);
  while (cursor.getTime() <= endDate.getTime()) {
    const chunkEndTime = Math.min(endDate.getTime(), cursor.getTime() + 30 * 24 * 60 * 60 * 1000);
    const chunkEnd = new Date(chunkEndTime);
    chunks.push({ startDayKey: ymdKey(cursor), endDayKey: ymdKey(chunkEnd) });
    cursor = new Date(chunkEnd.getTime() + 24 * 60 * 60 * 1000);
  }
  await Promise.all(chunks.map((c) => rebuild(c)));
}

// Reads the cached summary for a range; if some days are missing, tries a one-time backfill
// (silently skipped if the caller isn't admin) and retries, then live-computes whatever's
// still missing so the caller always gets complete data either way.
async function ensureSummaryCoverage(rangeStart: Date, rangeEndExclusive: Date): Promise<RangeResult> {
  if (rangeEndExclusive.getTime() <= rangeStart.getTime()) {
    return { detail: {}, newSourceDetail: {}, leadCache: {}, rows: [] };
  }
  let result = await loadSummaryRange(rangeStart, rangeEndExclusive);
  const expectedKeys = listDayKeys(rangeStart, rangeEndExclusive);
  let loadedKeys = new Set(result.rows.map((row) => row.key));
  let missing = expectedKeys.filter((k) => !loadedKeys.has(k));

  if (missing.length > 0) {
    await backfillDayRange(missing[0], missing[missing.length - 1]).catch(() => {});
    result = await loadSummaryRange(rangeStart, rangeEndExclusive);
    loadedKeys = new Set(result.rows.map((row) => row.key));
    missing = expectedKeys.filter((k) => !loadedKeys.has(k));
  }

  if (missing.length > 0) {
    const missingStart = dateFromDayKey(missing[0]);
    const missingEndExclusive = new Date(dateFromDayKey(missing[missing.length - 1]).getTime() + 24 * 60 * 60 * 1000);
    const liveResult = await loadForRange(missingStart, missingEndExclusive);
    result = mergeResults(result, liveResult);
  }

  return result;
}

const fmtMonthLabel = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

async function loadMonthAggregate(monthKey: string, currentMonthKey: string): Promise<CompareRow> {
  const [yearStr, monthStr] = monthKey.split('-');
  const year = Number(yearStr);
  const monthIdx = Number(monthStr) - 1;
  const start = new Date(year, monthIdx, 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(year, monthIdx + 1, 1);
  end.setHours(0, 0, 0, 0);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);
  const isCurrentMonth = monthKey === currentMonthKey;
  const summaryEnd = isCurrentMonth ? new Date(Math.min(end.getTime(), todayStart.getTime())) : end;

  let result = await ensureSummaryCoverage(start, summaryEnd);

  if (isCurrentMonth && todayStart.getTime() >= start.getTime() && todayStart.getTime() < end.getTime()) {
    const todayRaw = await loadForRange(todayStart, new Date(Math.min(end.getTime(), tomorrowStart.getTime())));
    result = mergeResults(result, todayRaw);
  }

  const totals = result.rows.reduce((acc, r) => {
    METRIC_KEYS.forEach((mk) => {
      (acc as any)[mk] += (r as any)[mk];
    });
    return acc;
  }, zeroTotals());

  return { key: monthKey, label: fmtMonthLabel(start), ...totals };
}

function monthKeyOffset(monthKey: string, offset: number): string {
  const [y, m] = monthKey.split('-').map((n) => Number(n));
  const d = new Date(y, (m - 1) + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthDailyMonthOptionsList() {
  const now = new Date();
  const out: Array<{ key: string; label: string }> = [];
  for (let i = 0; i < 12; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
    });
  }
  return out;
}

const COMPARE_BATCH_SIZE = 6;

export default function LeadVisitAnalyticsPanel(props: {
  active: boolean;
  leadStatuses: LeadStatusDef[];
  userDirectory: DirectoryUser[];
  onOpenLead: (lead: any) => void;
  detailLeadId: string | null;
}) {
  const { active, leadStatuses, userDirectory, onOpenLead, detailLeadId } = props;

  const statusLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of leadStatuses) {
      const label = String(s?.label || s?.name || s?.id || '').trim();
      if (!label) continue;
      m.set(String(s.id).toLowerCase(), label);
      m.set(label.toLowerCase(), label);
    }
    return m;
  }, [leadStatuses]);
  const statusLabel = useCallback(
    (lead: any) => {
      const raw = String(lead?.status || '').trim();
      if (!raw) return '—';
      return statusLabelMap.get(raw.toLowerCase()) || raw;
    },
    [statusLabelMap]
  );

  const emailToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of userDirectory) {
      const email = String(u?.email || '').trim().toLowerCase();
      const name = String(u?.name || '').trim();
      if (email && name) m.set(email, name);
    }
    return m;
  }, [userDirectory]);
  const assignedLabel = useCallback(
    (lead: any) => {
      const explicit = String(lead?.assignedToName || '').trim();
      if (explicit) return explicit;
      const email = String(lead?.assignedTo || '').trim().toLowerCase();
      if (!email) return 'Unassigned';
      if (email.includes('@')) return emailToName.get(email) || email.split('@')[0] || email;
      return email;
    },
    [emailToName]
  );

  const currentMonthKey = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const [monthDailyView, setMonthDailyView] = useState<'daily' | 'compare'>('daily');
  const [monthDailyKey, setMonthDailyKey] = useState<string>(currentMonthKey);
  const [monthDailyLoading, setMonthDailyLoading] = useState(false);
  const [monthDailyError, setMonthDailyError] = useState<string | null>(null);
  const [monthDailyOpen, setMonthDailyOpen] = useState<{ dayKey: string; metric: string } | null>(null);
  const [monthDailyDetail, setMonthDailyDetail] = useState<DetailMap>({});
  const [monthDailyDetailLoading, setMonthDailyDetailLoading] = useState(false);
  const [monthDailyNewSourceDetail, setMonthDailyNewSourceDetail] = useState<Record<string, Record<string, number>>>({});
  const [monthDailyLeadCache, setMonthDailyLeadCache] = useState<Record<string, any>>({});
  const [monthDailyRows, setMonthDailyRows] = useState<DailyRow[]>([]);

  const [compareRows, setCompareRows] = useState<CompareRow[]>([]);
  const [compareMonthsLoaded, setCompareMonthsLoaded] = useState(0);
  const [compareInitialLoading, setCompareInitialLoading] = useState(false);
  const [compareLoadingMore, setCompareLoadingMore] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);

  const monthDailyMonthOptions = useMemo(() => monthDailyMonthOptionsList(), []);

  const loadMonthDailyView = useCallback(async (targetMonthKey: string) => {
    setMonthDailyLoading(true);
    setMonthDailyError(null);
    try {
      const [yearStr, monthStr] = String(targetMonthKey || '').split('-');
      const year = Number(yearStr);
      const monthIdx = Number(monthStr) - 1;
      if (!Number.isFinite(year) || !Number.isFinite(monthIdx) || monthIdx < 0 || monthIdx > 11) {
        throw new Error('Invalid month selected.');
      }
      const start = new Date(year, monthIdx, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(year, monthIdx + 1, 1);
      end.setHours(0, 0, 0, 0);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const tomorrowStart = new Date(todayStart);
      tomorrowStart.setDate(tomorrowStart.getDate() + 1);
      const isCurrentMonth = targetMonthKey === currentMonthKey;
      const summaryEnd = isCurrentMonth ? new Date(Math.min(end.getTime(), todayStart.getTime())) : end;

      let result = await ensureSummaryCoverage(start, summaryEnd);

      if (isCurrentMonth && todayStart.getTime() >= start.getTime() && todayStart.getTime() < end.getTime()) {
        const todayRaw = await loadForRange(todayStart, new Date(Math.min(end.getTime(), tomorrowStart.getTime())));
        result = mergeResults(result, todayRaw);
      }

      setMonthDailyDetail(result.detail);
      setMonthDailyNewSourceDetail(result.newSourceDetail || {});
      setMonthDailyLeadCache(result.leadCache || {});
      setMonthDailyRows(result.rows);
    } catch (err: any) {
      console.error('month daily analytics failed', err);
      setMonthDailyError(err?.message || 'Failed to load monthly analytics.');
    } finally {
      setMonthDailyLoading(false);
    }
  }, [currentMonthKey]);

  useEffect(() => {
    if (!active || monthDailyView !== 'daily') return;
    loadMonthDailyView(monthDailyKey);
  }, [active, monthDailyView, monthDailyKey, loadMonthDailyView]);

  const loadMoreCompareMonths = useCallback(async () => {
    const isInitial = compareMonthsLoaded === 0;
    if (isInitial) setCompareInitialLoading(true);
    else setCompareLoadingMore(true);
    setCompareError(null);
    try {
      const nextKeys: string[] = [];
      for (let i = 0; i < COMPARE_BATCH_SIZE; i += 1) {
        nextKeys.push(monthKeyOffset(currentMonthKey, -(compareMonthsLoaded + i)));
      }
      const rows = await Promise.all(nextKeys.map((mk) => loadMonthAggregate(mk, currentMonthKey)));
      setCompareRows((prev) => [...prev, ...rows]);
      setCompareMonthsLoaded((prev) => prev + COMPARE_BATCH_SIZE);
    } catch (err: any) {
      console.error('compare months load failed', err);
      setCompareError(err?.message || 'Failed to load comparison data.');
    } finally {
      setCompareInitialLoading(false);
      setCompareLoadingMore(false);
    }
  }, [compareMonthsLoaded, currentMonthKey]);

  useEffect(() => {
    if (!active || monthDailyView !== 'compare') return;
    if (compareRows.length > 0 || compareInitialLoading) return;
    loadMoreCompareMonths();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, monthDailyView]);

  const monthDailyTotals = useMemo(() => {
    return monthDailyRows.reduce((acc, r) => {
      METRIC_KEYS.forEach((mk) => {
        (acc as any)[mk] += (r as any)[mk];
      });
      return acc;
    }, zeroTotals());
  }, [monthDailyRows]);

  const toggleMonthDailyOpen = useCallback((dayKey: string, metric: string) => {
    setMonthDailyOpen((prev) => (prev && prev.dayKey === dayKey && prev.metric === metric ? null : { dayKey, metric }));
  }, []);

  const monthDailyOpenInfo = useMemo(() => {
    const dayKey = monthDailyOpen?.dayKey || null;
    const metric = monthDailyOpen?.metric || null;
    const counts = dayKey && metric ? (monthDailyDetail?.[dayKey]?.[metric as MetricKey] || {}) : ({} as Record<string, number>);
    const total = Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
    const label = metric ? METRIC_LABELS[metric as MetricKey] || metric : '';
    return { dayKey, metric, counts, total, label };
  }, [monthDailyDetail, monthDailyOpen]);

  useEffect(() => {
    if (!monthDailyOpen?.dayKey || monthDailyDetail?.[monthDailyOpen.dayKey]) return;
    let cancelled = false;
    (async () => {
      try {
        setMonthDailyDetailLoading(true);
        const snap = await getDoc(doc(db, 'leadVisitDailySummary', monthDailyOpen.dayKey));
        if (cancelled) return;
        const data = snap.exists() ? (snap.data() as any) : null;
        const toCounts = (metric: MetricKey) =>
          Object.fromEntries(
            Object.entries(data?.metricLeadCounts?.[metric] || {}).map(([leadId, count]) => [leadId, Number(count) || 0])
          );
        const nextDayDetail = Object.fromEntries(METRIC_KEYS.map((mk) => [mk, toCounts(mk)])) as DetailMap[string];
        setMonthDailyDetail((prev) => ({ ...prev, [monthDailyOpen.dayKey]: nextDayDetail }));
      } catch (err) {
        if (!cancelled) console.error('month daily detail load failed', err);
      } finally {
        if (!cancelled) setMonthDailyDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [monthDailyDetail, monthDailyOpen]);

  const monthDailyOpenLeads = useMemo(() => {
    return Object.entries(monthDailyOpenInfo.counts || {})
      .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0))
      .map(([leadId, count]) => ({ leadId, count: Number(count) || 0, lead: monthDailyLeadCache?.[leadId] || null }))
      .filter((x) => Boolean(x.lead));
  }, [monthDailyLeadCache, monthDailyOpenInfo.counts]);

  useEffect(() => {
    const missingLeadIds = Object.keys(monthDailyOpenInfo.counts || {}).filter((leadId) => !monthDailyLeadCache?.[leadId]);
    if (missingLeadIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const nextRows = await fetchLeadsByIds(missingLeadIds);
      if (cancelled || Object.keys(nextRows).length === 0) return;
      setMonthDailyLeadCache((prev) => ({ ...prev, ...nextRows }));
    })();
    return () => {
      cancelled = true;
    };
  }, [monthDailyLeadCache, monthDailyOpenInfo.counts]);

  const insightPaperSx = {
    mb: 2,
    p: 1.5,
    borderRadius: 3,
    border: '1px solid',
    borderColor: (theme: any) => alpha(theme.palette.primary.main, 0.22),
    bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.04),
    boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
    position: 'relative' as const,
    overflow: 'hidden' as const,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      bgcolor: 'primary.main',
      opacity: 0.55,
    },
  };

  const compareTableHeader = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '120px repeat(9, minmax(0, 1fr))',
        px: 1.25,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.06),
      }}
    >
      {['Period', 'New', 'Visits', 'Revisit', 'Broker', 'C2C', 'Call', 'Walk-in', 'Ref', 'Other'].map((h, idx) => (
        <Typography
          key={h}
          sx={{
            fontSize: 12,
            fontWeight: 900,
            color: 'text.secondary',
            textAlign: 'center',
            ...(idx === 0
              ? {
                  position: 'sticky',
                  left: 0,
                  zIndex: 6,
                  bgcolor: 'background.paper',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                  textAlign: 'left',
                  pl: 0.25,
                }
              : {}),
          }}
        >
          {h}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Paper sx={insightPaperSx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 0 }}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
              color: 'primary.main',
            }}
          >
            <TodayOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 14, lineHeight: 1.1 }}>Lead & Visit Analytics</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {monthDailyView === 'compare' ? 'Rolling month-over-month trend' : ''}
            </Typography>
          </Box>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Stack direction="row" spacing={1}>
            <Button size="small" fullWidth variant={monthDailyView === 'daily' ? 'contained' : 'outlined'} onClick={() => setMonthDailyView('daily')}>
              Daily
            </Button>
            <Button size="small" fullWidth variant={monthDailyView === 'compare' ? 'contained' : 'outlined'} onClick={() => setMonthDailyView('compare')}>
              Compare
            </Button>
          </Stack>
          {monthDailyView === 'daily' && (
            <FormControl size="small" sx={{ minWidth: 140, width: { xs: '100%', sm: 'auto' } }}>
              <Select value={monthDailyKey} onChange={(e) => setMonthDailyKey(String(e.target.value || ''))}>
                {monthDailyMonthOptions.map((m) => (
                  <MenuItem key={m.key} value={m.key}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Stack>

      {monthDailyView === 'compare' ? (
        <>
          {compareError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {compareError}
            </Alert>
          )}
          {compareInitialLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading comparison…
              </Typography>
            </Box>
          ) : compareRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No comparison data available.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: { xs: 980, md: 0 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'visible', bgcolor: 'background.paper' }}>
                {compareTableHeader}
                {compareRows.map((r) => (
                  <Box
                    key={r.key}
                    sx={{ display: 'grid', gridTemplateColumns: '120px repeat(9, minmax(0, 1fr))', px: 1.25, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 800,
                        position: 'sticky',
                        left: 0,
                        zIndex: 5,
                        bgcolor: 'background.paper',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                        pl: 0.25,
                      }}
                    >
                      {r.label}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.newLeads}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.visits}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.revisit}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.brokerVisit}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.c2cVisit}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.callVisit}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.walkInVisit}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.referenceVisit}</Typography>
                    <Typography sx={{ fontSize: 12.5, textAlign: 'center' }}>{r.otherVisit}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
                <Button size="small" variant="outlined" disabled={compareLoadingMore} onClick={() => loadMoreCompareMonths()}>
                  {compareLoadingMore ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  ) : (
                    `Load ${COMPARE_BATCH_SIZE} more months`
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </>
      ) : (
        <>
          {monthDailyError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {monthDailyError}
            </Alert>
          )}

          {monthDailyLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading analytics…
              </Typography>
            </Box>
          ) : monthDailyRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No data yet for selected month.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: { xs: 980, md: 0 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'visible', bgcolor: 'background.paper' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '120px repeat(9, minmax(0, 1fr))',
                    gap: 0,
                    px: 1.25,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                  }}
                >
                  {['Date', 'New', 'Visits', 'Revisit', 'Broker', 'C2C', 'Call', 'Walk-in', 'Ref', 'Other'].map((h, idx) => (
                    <Typography
                      key={h}
                      sx={{
                        fontSize: 12,
                        fontWeight: 900,
                        color: 'text.secondary',
                        textAlign: 'center',
                        ...(idx === 0
                          ? {
                              position: 'sticky',
                              left: 0,
                              zIndex: 6,
                              bgcolor: 'background.paper',
                              borderRight: '1px solid',
                              borderColor: 'divider',
                              boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                              textAlign: 'left',
                              pl: 0.25,
                            }
                          : {}),
                      }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>

                {monthDailyRows
                  .slice()
                  .reverse()
                  .map((r) => {
                    const isOpen = monthDailyOpenInfo.dayKey === r.key;
                    const activeMetric = monthDailyOpenInfo.metric;

                    const renderCell = (value: number, metric: MetricKey) => {
                      const active = isOpen && activeMetric === metric;
                      if (value <= 0) {
                        return <Typography sx={{ fontSize: 12.5, opacity: 0.7, textAlign: 'center' }}>{value}</Typography>;
                      }
                      const cell = (
                        <ButtonBase
                          onClick={() => toggleMonthDailyOpen(r.key, metric)}
                          sx={{
                            justifyContent: 'center',
                            width: '100%',
                            minHeight: 26,
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.35,
                            ...(active ? { bgcolor: (theme: any) => alpha(theme.palette.primary.main, 0.1) } : {}),
                            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) },
                          }}
                        >
                          <Typography sx={{ fontSize: 12.5, fontWeight: active ? 900 : 700, color: 'primary.main', textAlign: 'center' }}>
                            {value}
                          </Typography>
                        </ButtonBase>
                      );
                      if (metric !== 'newLeads') return cell;
                      const sourceCounts = monthDailyNewSourceDetail?.[r.key] || {};
                      const rows = Object.entries(sourceCounts).sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0));
                      if (rows.length === 0) return cell;
                      return (
                        <Tooltip
                          arrow
                          placement="top"
                          title={
                            <Box sx={{ py: 0.25 }}>
                              <Typography sx={{ fontSize: 11.5, fontWeight: 800, mb: 0.5 }}>New Leads by Source</Typography>
                              {rows.map(([src, cnt]) => (
                                <Box key={`${r.key}-${src}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                                  <Typography sx={{ fontSize: 11.5 }}>{src}</Typography>
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>{cnt}</Typography>
                                </Box>
                              ))}
                            </Box>
                          }
                        >
                          <Box sx={{ display: 'inline-flex', width: '100%' }}>{cell}</Box>
                        </Tooltip>
                      );
                    };

                    return (
                      <React.Fragment key={r.key}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '120px repeat(9, minmax(0, 1fr))', px: 1.25, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 800,
                              textAlign: 'left',
                              position: 'sticky',
                              left: 0,
                              zIndex: 5,
                              bgcolor: 'background.paper',
                              borderRight: '1px solid',
                              borderColor: 'divider',
                              boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                              pl: 0.25,
                            }}
                          >
                            {r.key}
                          </Typography>
                          {renderCell(r.newLeads, 'newLeads')}
                          {renderCell(r.visits, 'visits')}
                          {renderCell(r.revisit, 'revisit')}
                          {renderCell(r.brokerVisit, 'brokerVisit')}
                          {renderCell(r.c2cVisit, 'c2cVisit')}
                          {renderCell(r.callVisit, 'callVisit')}
                          {renderCell(r.walkInVisit, 'walkInVisit')}
                          {renderCell(r.referenceVisit, 'referenceVisit')}
                          {renderCell(r.otherVisit, 'otherVisit')}
                        </Box>

                        <Collapse in={isOpen} timeout={200} unmountOnExit>
                          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03), px: 1.25, py: 1 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 900 }}>
                                {monthDailyOpenInfo.label} — {monthDailyOpenInfo.total}
                              </Typography>
                              <Button size="small" onClick={() => setMonthDailyOpen(null)}>
                                Close
                              </Button>
                            </Stack>

                            {monthDailyDetailLoading ? (
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  Loading day details…
                                </Typography>
                              </Stack>
                            ) : monthDailyOpenLeads.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No leads to show.
                              </Typography>
                            ) : (
                              <Stack spacing={0}>
                                {monthDailyOpenLeads.map(({ lead, leadId, count }) => (
                                  <Box key={leadId} sx={{ position: 'relative' }}>
                                    <LeadCompactCard
                                      lead={lead}
                                      view="sheet"
                                      selected={detailLeadId === leadId}
                                      onClick={() => onOpenLead(lead)}
                                      statusLabel={statusLabel(lead)}
                                      assignedLabel={assignedLabel(lead)}
                                      badge={null}
                                    />
                                    {count > 1 && (
                                      <Chip
                                        size="small"
                                        label={`x${count}`}
                                        sx={{ position: 'absolute', right: 12, top: 10, height: 18, fontSize: 11, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) }}
                                      />
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            )}
                          </Box>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '120px repeat(9, minmax(0, 1fr))',
                    px: 1.25,
                    py: 0.8,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 900,
                      textAlign: 'left',
                      position: 'sticky',
                      left: 0,
                      zIndex: 5,
                      bgcolor: 'background.paper',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '2px 0 0 rgba(15, 23, 42, 0.06)',
                      pl: 0.25,
                    }}
                  >
                    Total
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.newLeads}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.visits}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.revisit}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.brokerVisit}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.c2cVisit}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.callVisit}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.walkInVisit}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.referenceVisit}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, textAlign: 'center' }}>{monthDailyTotals.otherVisit}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
